import React, {
    createContext,
    useState,
    useCallback,
    useEffect,
    useRef,
    ReactNode,
    useContext,
} from 'react';
import { toast } from 'react-toastify';
import {
    SimulationStep,
    FormDataCLT,
    FinanceiraCard,
    FinanceiraStatus,
    FinanceiraOption,
    PropostaConsultaItem,
    SimulacaoParams,
    SimulacaoResult,
    OfertaSelecionada,
    FormDataDigitacao,
    PropostaResultado,
    DigitacaoFormField,
    DigitacaoValidationFieldError,
    PartnerCommissionResult,
    HistoricoSimulacaoItem,
    ScreeningConsultData,
    UltimaSimulacao,
    COMPETENCE_WINDOW_ERROR_CODE,
} from '../../models/SimulationBank/SimulationBankModels';
import { SimulationBankService } from '../../services/SimulationBank/SimulationBankService';
import { useAuth } from '../AuthProvider';
import { getFinanceiraLogo, getFinanceiraLogoMobile } from '../../utils/FinanceiraLogoMap';
import {
    loadSimulationSession,
    saveSimulationSession,
    clearSimulationSession,
} from '../../utils/SimulationSessionCache';
import {
    applyDigitacaoInjections,
    applyHistoricoPrefill,
    detectFieldKind,
    detectFieldKindFromMensagem,
    parseFieldErrorsFromMensagem,
} from '../../components/Chats/SimulationBank/steps/StepDigitacao/digitacaoFieldRules';
interface SimulationBankContextProps {
    step: SimulationStep;
    formData: FormDataCLT | null;
    financeiras: FinanceiraCard[];
    screeningId: string | number | null;
    simulacaoId: string | number | null;
    resultados: SimulacaoResult[];
    simParams: SimulacaoParams | null;
    hasOptionalData: boolean;
    ultimaSimulacao: UltimaSimulacao | null;
    clienteConhecido: boolean | null;
    ofertaSelecionada: OfertaSelecionada | null;
    formDataDigitacao: FormDataDigitacao | null;
    propostaResultado: PropostaResultado | null;
    digitacaoForm: DigitacaoFormField[] | null;
    isLoadingTriagem: boolean;
    isLoadingSimulation: boolean;
    simulateError: boolean;
    isSubmittingProposta: boolean;
    isLoadingDigitacaoForm: boolean;
    isPolling: boolean;
    isValidatingBmg: boolean;
    loggedPartnerCommission: PartnerCommissionResult | null;
    isCheckingLoggedCommission: boolean;
    checkLoggedPartnerCommission: () => Promise<void>;
    typingAgent: PartnerCommissionResult | null;
    isCheckingTypingAgent: boolean;
    typingAgentError: string | null;
    validateTypingAgentCode: (partnerCode: string) => Promise<void>;
    clearTypingAgent: () => void;
    competenceWindowMessage: string | null;
    dismissCompetenceWindow: () => void;
    checkScreeningAvailability: () => Promise<boolean>;
    getDataprevBlockStatus: () => Promise<boolean | null>;
    setDataprevBlockStatus: (isBlocked: boolean) => Promise<boolean>;
    listFinanceirasParaConsulta: () => Promise<FinanceiraOption[]>;
    getLastSelectedFinancials: () => Promise<string[]>;
    queryPropostas: (payload: Record<string, any>) => Promise<{ success: boolean; propostas: PropostaConsultaItem[]; message?: string }>;
    queryHistoricoSimulacoes: (params?: { limit?: number; offset?: number }) => Promise<{ success: boolean; simulacoes: HistoricoSimulacaoItem[]; total: number; message?: string }>;
    setStep: (step: SimulationStep) => void;
    setFormData: (data: FormDataCLT) => void;
    handleTriagem: (data: FormDataCLT) => Promise<boolean>;
    handleScreeningPoll: () => Promise<{ done: boolean; count: number }>;
    setSimParams: (params: SimulacaoParams) => void;
    handleSimulate: (params: SimulacaoParams) => Promise<void>;
    setOfertaSelecionada: (oferta: OfertaSelecionada | null) => void;
    setFormDataDigitacao: (data: FormDataDigitacao) => void;
    handleLoadDigitacaoForm: () => Promise<boolean>;
    handleValidateDigitacao: (dados: FormDataDigitacao) => Promise<{ success: boolean; fieldErrors: DigitacaoValidationFieldError[] }>;
    isValidatingDigitacao: boolean;
    handleSubmitProposta: () => Promise<void>;
    startPolling: (id: string | number) => void;
    stopPolling: () => void;
    toggleFinanceira: (id: number | string) => void;
    handleValidateBmgCode: (codigo: string) => Promise<boolean>;
    reset: () => void;
}

const normalizeCanonicalId = (id: unknown): string => String(id ?? '').toUpperCase();

// BMG fica de fora: não participa do screening/poll (ver handleValidateBmgCode).
const getExpectedFinanceiras = (selecionadas?: string[] | null): string[] | null =>
    Array.isArray(selecionadas)
        ? selecionadas.map(normalizeCanonicalId).filter((id) => id !== 'BMG')
        : null;

// Card oculto (NAO_MOSTRAR) também é desfecho final: o mergeResults só marca `hidden`
// num card já existente, sem mexer no status — que pode continuar PRE_ANALISE e, sem
// esse caso, seguraria o poll pra sempre.
const isFinanceiraTerminal = (f: FinanceiraCard): boolean =>
    !!f.hidden || f.status === 'CONCLUIDO' || f.status === 'ERRO';

export const SimulationBankContext = createContext<SimulationBankContextProps>(
    {} as SimulationBankContextProps
);

export const SimulationBankProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [cachedSession] = useState(() => loadSimulationSession()); // Persist session across page refresh

    const [step, setStep] = useState<SimulationStep>(cachedSession?.step ?? 1);
    const [formData, setFormData] = useState<FormDataCLT | null>(cachedSession?.formData ?? null);
    const [financeiras, setFinanceiras] = useState<FinanceiraCard[]>(cachedSession?.financeiras ?? []);
    // Espelha `financeiras` pra handleScreeningPoll ler o estado mais recente sem precisar
    // depender dele (o que recriaria a callback — e reiniciaria o timer de polling em
    // Step3Cards — a cada atualização de card).
    const financeirasRef = useRef<FinanceiraCard[]>(financeiras);
    useEffect(() => {
        financeirasRef.current = financeiras;
    }, [financeiras]);

    const [screeningId, setScreeningId] = useState<string | number | null>(cachedSession?.screeningId ?? null);
    const [simulacaoId, setSimulacaoId] = useState<string | number | null>(cachedSession?.simulacaoId ?? null);
    const [resultados, setResultados] = useState<SimulacaoResult[]>(cachedSession?.resultados ?? []);
    const [simParams, setSimParams] = useState<SimulacaoParams | null>(cachedSession?.simParams ?? null);
    const [hasOptionalData, setHasOptionalData] = useState(cachedSession?.hasOptionalData ?? false);
    // SB-1953 — histórico do cliente devolvido pelo /consult. `clienteConhecido` é
    // tri-estado de propósito: null é "o integrador ainda não subiu o SB-1953 e a chave
    // não veio"; false é a informação de que o cliente é novo.
    const [ultimaSimulacao, setUltimaSimulacao] = useState<UltimaSimulacao | null>(cachedSession?.ultimaSimulacao ?? null);
    const [clienteConhecido, setClienteConhecido] = useState<boolean | null>(cachedSession?.clienteConhecido ?? null);
    const [ofertaSelecionada, setOfertaSelecionada] = useState<OfertaSelecionada | null>(cachedSession?.ofertaSelecionada ?? null);
    const [formDataDigitacao, setFormDataDigitacao] = useState<FormDataDigitacao | null>(cachedSession?.formDataDigitacao ?? null);
    const [propostaResultado, setPropostaResultado] = useState<PropostaResultado | null>(cachedSession?.propostaResultado ?? null);
    const [digitacaoForm, setDigitacaoForm] = useState<DigitacaoFormField[] | null>(cachedSession?.digitacaoForm ?? null);
    const [digitacaoIdCadastro, setDigitacaoIdCadastro] = useState<string | null>(cachedSession?.digitacaoIdCadastro ?? null);
    // Comissionamento do parceiro logado e, quando aplicável, do agente de digitação
    // validado no espelho (StepConfirmacao) — ver checkLoggedPartnerCommission/
    // validateTypingAgentCode e handleSubmitProposta (logged_typing_agent_id).
    const [loggedPartnerCommission, setLoggedPartnerCommission] = useState<PartnerCommissionResult | null>(null);
    const [isCheckingLoggedCommission, setIsCheckingLoggedCommission] = useState(false);
    const [typingAgent, setTypingAgent] = useState<PartnerCommissionResult | null>(cachedSession?.typingAgent ?? null);
    const [isCheckingTypingAgent, setIsCheckingTypingAgent] = useState(false);
    const [typingAgentError, setTypingAgentError] = useState<string | null>(null);
    const [isLoadingTriagem, setIsLoadingTriagem] = useState(false);
    const [isLoadingSimulation, setIsLoadingSimulation] = useState(false);
    const [simulateError, setSimulateError] = useState(false);
    const [isSubmittingProposta, setIsSubmittingProposta] = useState(false);
    const [isLoadingDigitacaoForm, setIsLoadingDigitacaoForm] = useState(false);
    const [isValidatingDigitacao, setIsValidatingDigitacao] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const [isValidatingBmg, setIsValidatingBmg] = useState(false);
    const [competenceWindowMessage, setCompetenceWindowMessage] = useState<string | null>(null);

    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // BMG é consultado por uma rota própria (validateBmgAuthorization), fora do
    // screening/poll normal — por isso o card sintético não nasce de uma resposta do
    // backend que respeite o `financeiras` escolhido no modal (ver FinanceirasConsentModal).
    // Sem esse guard, o card aparecia sempre, mesmo com o parceiro tendo desmarcado o BMG
    // na etapa de autorização. Default true: chamadas antigas de handleTriagem sem
    // `financeiras` (nenhuma seleção feita) mantêm o comportamento de sempre oferecer o BMG.
    const bmgSelectedRef = useRef(true);
    // Canonical_ids (exceto BMG, que não passa pelo poll) escolhidos no modal de
    // financeiras — é a quantidade que o poll precisa ver concluída pra parar, em vez de
    // esperar um total "máximo" de financeiras. null = sem seleção (fluxo antigo): o
    // critério volta a ser só "todas as conhecidas terminaram".
    const expectedFinanceirasRef = useRef<string[] | null>(
        getExpectedFinanceiras(cachedSession?.formData?.financeiras),
    );
    const service = new SimulationBankService();
    const { userLogged } = useAuth();

    const buildBmgCard = (): FinanceiraCard => ({
        id: 'BMG',
        canonical_id: 'BMG',
        nome: 'Banco BMG',
        logo: getFinanceiraLogo('BMG'),
        logoMobile: getFinanceiraLogoMobile('BMG'),
        probabilidade: 'alta',
        status: 'AGUARDANDO_CODIGO',
        selecionada: false,
    });

    const dismissCompetenceWindow = useCallback(() => setCompetenceWindowMessage(null), []);

    const checkCompetenceWindow = (response: any): boolean => {
        if (response?.data?.code !== COMPETENCE_WINDOW_ERROR_CODE) return false;
        setCompetenceWindowMessage(response.message || 'Todo mês, entre os dias 20 e 23, a Dataprev pausa as consultas de margem para atualização.\nA simulação volta a funcionar normalmente durante o dia 23.');
        return true;
    };

    const getFriendlyTriagemError = (response: any, fallback: string): string =>
        typeof response?.message === 'string' && response.message.includes('FINANCEIRAS_ALVO')
            ? 'Nenhuma financeira disponível no momento.'
            : fallback;

    const checkScreeningAvailability = useCallback(async (): Promise<boolean> => {
        const [response, error] = await service.getSimulationBlockStatus();
        if (error) return true;

        if (response?.data?.simulation_block) {
            setCompetenceWindowMessage(response.data.message || 'Todo mês, entre os dias 20 e 23, a Dataprev pausa as consultas de margem para atualização.\nA simulação volta a funcionar normalmente durante o dia 23.');
            return false;
        }

        return true;
    }, []);

    // Consulta o estado atual de bloqueio manual da Dataprev (tela de seleção de
    // produto), sem os efeitos colaterais de checkScreeningAvailability (que dispara
    // a tela cheia de "indisponível" — aqui só queremos o rótulo do botão).
    const getDataprevBlockStatus = useCallback(async (): Promise<boolean | null> => {
        const [response, error] = await service.getSimulationBlockStatus();
        if (error) return null;
        return !!response?.data?.simulation_block;
    }, []);

    const setDataprevBlockStatus = useCallback(async (isBlocked: boolean): Promise<boolean> => {
        const [, error] = await service.setSimulationBlockStatus(isBlocked);
        return !error;
    }, []);

    // Não filtra por is_active: o usuário pode precisar consultar proposta de financeira já inativada.
    const listFinanceirasParaConsulta = useCallback(async (): Promise<FinanceiraOption[]> => {
        const [response, error] = await service.getFinanceiras();
        if (error) {
            toast.error(response?.message || error);
            return [];
        }
        return response?.data ?? [];
    }, []);

    // Canonical_ids usados na simulação mais recente do usuário (simulation_sessions),
    // para pré-marcar os tiles do FinanceirasConsentModal. Vazio quando o usuário nunca
    // simulou ou a última sessão não tem `selected_financials`.
    const getLastSelectedFinancials = useCallback(async (): Promise<string[]> => {
        const [response, error] = await service.getLastSelectedFinancials();
        if (error) {
            toast.error(response?.message || error);
            return [];
        }
        return response?.data ?? [];
    }, []);

    // Consulta de Propostas (por data de digitação/alteração, CPF ou financeira+número) —
    // ver handleQuerySubmit/handleQueryCpfSubmit/handleQueryPropostaSubmit em SimulationBank.tsx.
    const queryPropostas = useCallback(async (payload: Record<string, any>): Promise<{ success: boolean; propostas: PropostaConsultaItem[]; message?: string }> => {
        const [response, error] = await service.listDigitations(payload);
        if (error) {
            return { success: false, propostas: [], message: response?.message || error };
        }
        const propostas: PropostaConsultaItem[] = response?.data?.data?.data ?? [];
        return { success: true, propostas };
    }, []);

    // Histórico de simulações (SB-1953) — ver ramo "Histórico de Simulações" da Consulta
    // de Propostas em SimulationBank.tsx. O 403 ganha mensagem própria: é token válido sem
    // a permissão cadastrada no integrador, não uma falha genérica que "tentar de novo"
    // resolva.
    const queryHistoricoSimulacoes = useCallback(async (params?: { limit?: number; offset?: number }): Promise<{ success: boolean; simulacoes: HistoricoSimulacaoItem[]; total: number; message?: string }> => {
        const [response, error, status] = await service.listSimulationHistory(params);
        if (error) {
            const message = status === 403
                ? 'Seu usuário não tem permissão para consultar o histórico de simulações. Fale com o suporte para liberar o acesso.'
                : (response?.message || error);
            return { success: false, simulacoes: [], total: 0, message };
        }
        const data = response?.data?.data ?? response?.data ?? {};
        return { success: true, simulacoes: data.SIMULACOES ?? [], total: data.TOTAL ?? 0 };
    }, []);

    const processFinanceiras = (rawList: any[]): FinanceiraCard[] =>
        rawList.map((f: any) => ({
            id: f.id,
            canonical_id: f.canonical_id,
            nome: f.nome ?? f.canonical_id ?? String(f.id),
            logo: f.logo_url || getFinanceiraLogo(f.canonical_id),
            logoMobile: f.logo_url || getFinanceiraLogoMobile(f.canonical_id),
            probabilidade: (f.probabilidade ?? 'alta') as 'alta' | 'baixa',
            status: 'PRE_ANALISE' as FinanceiraStatus,
            selecionada: f.probabilidade !== 'baixa',
        }));

    const mergePreAnalise = (prev: FinanceiraCard[], items: any[]): FinanceiraCard[] => {
        const merged = [...prev];
        items.forEach((f: any) => {
            // BMG nunca participa do polling normal (ver buildBmgCard/handleValidateBmgCode) —
            // ignora qualquer item com esse canonical_id pra não pisar no card sintético local.
            if (String(f.canonical_id).toUpperCase() === 'BMG') return;
            let idx = merged.findIndex((m) => m.id === f.id);
            if (idx < 0 && f.canonical_id) {
                idx = merged.findIndex((m) => m.canonical_id === f.canonical_id);
            }
            const patch: Partial<FinanceiraCard> = {
                status: f.status ?? ((f.link_url || f.already_authorized) ? 'AGUARDANDO_AUTORIZACAO' : 'PRE_ANALISE'),
                link_url: f.link_url ?? undefined,
                already_authorized: f.already_authorized ?? undefined,
                canonical_id: f.canonical_id,
            };
            if (idx >= 0) {
                merged[idx] = { ...merged[idx], ...patch };
            } else {
                const existingHidden = merged.find((m) => m.canonical_id === f.canonical_id && m.hidden);
                if (!existingHidden) {
                    merged.push({
                        id: f.id,
                        canonical_id: f.canonical_id,
                        nome: f.canonical_id ?? String(f.id),
                        logo: getFinanceiraLogo(f.canonical_id),
                        logoMobile: getFinanceiraLogoMobile(f.canonical_id),
                        probabilidade: 'alta',
                        selecionada: false,
                        ...patch,
                    } as FinanceiraCard);
                }
            }
        });
        return merged;
    };

    const mergeResults = (prev: FinanceiraCard[], items: any[], hasOptionalDataValue: boolean): FinanceiraCard[] => {
        let merged = [...prev];
        items.forEach((r: any) => {
            // BMG nunca participa do polling normal (ver buildBmgCard/handleValidateBmgCode) —
            // ignora qualquer item com esse canonical_id pra não pisar no card sintético local.
            if (String(r.canonical_id).toUpperCase() === 'BMG') return;

            const classification = r.classification?.toUpperCase();

            // Tenta match por id, senão por canonical_id
            let idx = merged.findIndex((m) => m.id === r.id);
            if (idx < 0 && r.canonical_id) {
                idx = merged.findIndex((m) => m.canonical_id === r.canonical_id);
            }

            if (classification === 'NAO_MOSTRAR') {
                const mensagemTratada = r.mensagem_tratada ?? undefined;
                if (idx >= 0) {
                    merged[idx] = { ...merged[idx], hidden: true, mensagem_tratada: mensagemTratada };
                } else {
                    merged.push({
                        id: r.id,
                        canonical_id: r.canonical_id,
                        nome: r.canonical_id ?? String(r.id),
                        logo: getFinanceiraLogo(r.canonical_id),
                        logoMobile: getFinanceiraLogoMobile(r.canonical_id),
                        probabilidade: 'baixa',
                        status: 'CONCLUIDO',
                        selecionada: false,
                        hidden: true,
                        mensagem_tratada: mensagemTratada,
                    } as FinanceiraCard);
                }
                return;
            }

            const prob: 'alta' | 'baixa' | 'disponivel' =
                classification === 'ALTA' ? 'alta'
                : classification === 'DISPONIVEL' ? 'disponivel'
                : 'baixa';
            const patch: Partial<FinanceiraCard> = {
                id: r.id,
                canonical_id: r.canonical_id,
                status: 'CONCLUIDO',
                probabilidade: prob,
                valor: r.valor ?? undefined,
                prazo_min: r.prazo_min ?? undefined,
                prazo_max: r.prazo_max ?? undefined,
                parcela_min: r.parcela_min ?? undefined,
                parcela_max: r.parcela_max ?? undefined,
                // Com hasOptionalData=false, o Step3Cards só renderiza a seção "disponivel"
                // (alta/baixa ficam ocultos) — não podemos auto-selecionar algo que o usuário
                // nunca viu, senão ele entra escondido no payload do /simulate e trava o
                // carregamento em Step2Loading (resultados nunca alcança o total selecionado).
                selecionada: hasOptionalDataValue ? prob !== 'baixa' : prob === 'disponivel',
                link_url: undefined,
                // Limpa a flag da fase de pré-análise: senão o card fica marcado como
                // "não selecionável" para sempre mesmo depois de virar resultado (CONCLUIDO).
                already_authorized: undefined,
            };
            if (idx >= 0) {
                merged[idx] = { ...merged[idx], ...patch };
            } else {
                merged.push({
                    nome: r.canonical_id ?? String(r.id),
                    logo: getFinanceiraLogo(r.canonical_id),
                    logoMobile: getFinanceiraLogoMobile(r.canonical_id),
                    ...patch,
                } as FinanceiraCard);
            }
        });
        return merged;
    };


    // Inicia triagem — trata dois cenários:
    // 1) backend retorna financeiras diretamente (consult síncrono)
    // 2) backend retorna screeningId para polling assíncrono
    const handleTriagem = useCallback(async (data: FormDataCLT): Promise<boolean> => {
        // Limpa estado anterior e cache antes de iniciar nova sessão
        clearSimulationSession();
        setScreeningId(null);
        setSimulacaoId(null);
        setHasOptionalData(true);
        setUltimaSimulacao(null);
        setClienteConhecido(null);

        // Sem `financeiras` (fluxo antigo/sem passar pelo modal), mantém o BMG disponível
        // como sempre foi — só a seleção explícita do parceiro pode tirá-lo da tela.
        bmgSelectedRef.current = data.financeiras ? data.financeiras.includes('BMG') : true;
        expectedFinanceirasRef.current = getExpectedFinanceiras(data.financeiras);

        // BMG não participa do screening/poll normal (ver handleValidateBmgCode) — se ele for
        // a ÚNICA financeira escolhida no modal, nenhuma outra jamais "chega" pra dar carona
        // pro card sintético, e a tela de loading ficava presa pra sempre (Step2Loading só
        // avança quando `financeiras.length > 0`). Por isso o card já nasce aqui, antes de
        // qualquer resposta do backend, em vez de esperar a primeira leva real de financeiras.
        const initialFinanceiras = bmgSelectedRef.current ? [buildBmgCard()] : [];
        setFinanceiras(initialFinanceiras);
        financeirasRef.current = initialFinanceiras;

        setIsLoadingTriagem(true);
        const [response, error] = await service.getTriagem(data);
        setIsLoadingTriagem(false);

        if (error) {
            if (checkCompetenceWindow(response)) return false;
            toast.error(getFriendlyTriagemError(response, response?.message || error));
            return false;
        }

        setFormData(data);

        // SB-1953 — `data` passou a ser um objeto; antes era a string do journey_id.
        const consultData: ScreeningConsultData =
            response?.data && typeof response.data === 'object' && !Array.isArray(response.data)
                ? response.data
                : {};
        // Chave ausente ≠ false: sem o SB-1953 no integrador nada vem, e aí não sabemos se
        // o cliente é conhecido (fica null, ver comentário do estado).
        setClienteConhecido(typeof consultData.CLIENTE_CONHECIDO === 'boolean' ? consultData.CLIENTE_CONHECIDO : null);
        // Chave ausente = cliente sem histórico (pelo contrato nunca vem null).
        setUltimaSimulacao(consultData.ULTIMA_SIMULACAO ?? null);

        // Cenário 1: financeiras já retornadas no consult
        const rawList =
            response?.data?.financeiras ??
            response?.financeiras ??
            (Array.isArray(response?.data) ? response.data : null);

        if (Array.isArray(rawList) && rawList.length > 0) {
            const lista = processFinanceiras(rawList);
            const financeirasComBmg = bmgSelectedRef.current ? [...lista, buildBmgCard()] : lista;
            setFinanceiras(financeirasComBmg);
            financeirasRef.current = financeirasComBmg;
            return true;
        }

        // Cenário 2: guarda screeningId para polling.
        // O journey_id agora vem em data.JOURNEY_ID (SB-1953); antes o consult devolvia a
        // string UUID direto em data ({ data: "uuid", status: true }). As duas leituras
        // antigas seguem na cadeia como retaguarda pra janela em que front e backend
        // estiverem em versões diferentes — sem o id, o /poll seguinte não tem o que pedir.
        const id = typeof response?.data === 'string'
            ? response.data
            : (consultData.JOURNEY_ID ?? response?.data?.journey_id ?? response?.data?.id ?? response?.journey_id ?? response?.id);

        if (id != null) setScreeningId(id);

        return true;
    }, []);

    // Chama /simulation/screening/poll — merge de pre_analise e results conforme chegam.
    // Retorna true (para o polling automático em Step3Cards) só quando todas as financeiras
    // conhecidas já chegaram a um estado terminal.
    // Retorna o total de financeiras atual (merged.length) junto com `done` pra quem chama
    // não precisar ler `financeiras` via estado/ref próprio — isso já causou uma corrida
    // (ver Step2Loading.tsx): o `done=true` podia chegar antes do React propagar o
    // setFinanceiras(merged) feito aqui dentro, então um ref espelhando `financeiras.length`
    // ficava momentaneamente desatualizado (0) e disparava "Nenhuma financeira encontrada"
    // à toa, por uma fração de segundo, antes do re-render corrigir a tela.
    const handleScreeningPoll = useCallback(async (): Promise<{ done: boolean; count: number }> => {
        if (screeningId == null) return { done: false, count: financeirasRef.current.length };
        const [response, error] = await service.pollScreening(screeningId);
        if (error) {
            if (checkCompetenceWindow(response)) return { done: true, count: financeirasRef.current.length };
            return { done: false, count: financeirasRef.current.length };
        }

        const preAnalise: any[] = response?.data?.pre_analise ?? [];
        const results: any[] = response?.data?.results ?? [];

        const hasOptionalDataValue = typeof response?.data?.has_optional_data === 'boolean'
            ? response.data.has_optional_data
            : hasOptionalData;
        if (typeof response?.data?.has_optional_data === 'boolean') {
            setHasOptionalData(hasOptionalDataValue);
        }

        // O card do BMG (quando selecionado) já nasce em handleTriagem, antes desta função
        // rodar pela primeira vez — não precisa (nem deve) ser injetado aqui de novo.
        let merged = financeirasRef.current;
        if (preAnalise.length > 0 || results.length > 0) {
            merged = mergePreAnalise(merged, preAnalise);
            merged = mergeResults(merged, results, hasOptionalDataValue);
            financeirasRef.current = merged;
            setFinanceiras(merged);
        }

        // Só para de pollar quando todas as financeiras conhecidas (exceto BMG, que depende
        // de validação manual do código por SMS — ver handleValidateBmgCode) chegaram a um
        // estado terminal. Antes, o critério era "4 CONCLUIDO no total" (contando também
        // cards ocultos/sem oferta), o que fazia o loop parar cedo demais e deixar
        // financeiras ainda pendentes (ex.: C6_BANK aguardando margem) travadas sem nunca
        // mais serem repolladas — só o botão "Atualizar Financeira" (que ignora esse
        // retorno) conseguia trazer a atualização.
        const semBmg = merged.filter((f) => normalizeCanonicalId(f.canonical_id) !== 'BMG');
        if (semBmg.some((f) => !isFinanceiraTerminal(f))) return { done: false, count: merged.length };

        // Com seleção feita no modal, o alvo é exatamente a quantidade escolhida: só para
        // quando cada financeira selecionada já voltou do backend com desfecho final — sem
        // isso, uma primeira leva parcial (ex.: 2 de 3 já concluídas) encerrava o poll antes
        // da terceira chegar.
        const expected = expectedFinanceirasRef.current;
        if (expected) {
            const terminais = new Set(semBmg.map((f) => normalizeCanonicalId(f.canonical_id)));
            return { done: expected.every((id) => terminais.has(id)), count: merged.length };
        }
        return { done: merged.length > 0, count: merged.length };
    }, [screeningId, hasOptionalData]);

    // Valida o código de autorização enviado por SMS ao cliente (peculiaridade do BMG,
    // ver Step3Cards) — diferente das demais financeiras, o BMG não é acompanhado pelo
    // polling normal: aguardamos a resposta desta chamada para então decidir o desfecho:
    // - aprovado (status_etapa === 'CONCLUIDO'): card vai pra CONCLUIDO com o resultado
    //   já embutido na resposta;
    // - cliente não elegível (elegivel === false / status_etapa === 'NAO_ELEGIVEL'): a API
    //   responde 200 normalmente — card vai pra seção "sem oferta" (hidden), sem retry;
    // - erro de integração real (4xx) ou código incorreto: card permanece em
    //   AGUARDANDO_CODIGO (não vira ERRO) — só um aviso é exibido, pra o usuário poder
    //   digitar o código novamente sem perder a etapa.
    const handleValidateBmgCode = useCallback(async (codigo: string): Promise<boolean> => {
        if (screeningId == null) return false;
        setIsValidatingBmg(true);
        const [response, error] = await service.validateBmgAuthorization({
            journey_id: String(screeningId),
            codigo_autorizacao: Number(codigo),
        });
        setIsValidatingBmg(false);

        if (error || !response?.status) {
            setFinanceiras((prev) => prev.map((f) => f.canonical_id === 'BMG'
                ? { ...f, status: 'AGUARDANDO_CODIGO' as FinanceiraStatus, erro: 'Não foi possível validar o código agora. Tente novamente.' }
                : f));
            return false;
        }

        const data = response?.data ?? {};
        const approved = data.status_etapa === 'CONCLUIDO';
        // Cliente não elegível para o BMG: a API responde com sucesso (200) e
        // data.elegivel === false / status_etapa === 'NAO_ELEGIVEL' — não é um problema do
        // código digitado (não faz sentido deixar o usuário tentar de novo), então tratamos
        // igual às demais financeiras sem oferta válida (ver mergeResults/NAO_MOSTRAR): o
        // card sai da lista de pendentes e vai para a seção "não apresentaram propostas válidas".
        const naoElegivel = data.elegivel === false || data.status_etapa === 'NAO_ELEGIVEL';
        setFinanceiras((prev) => prev.map((f) => {
            if (f.canonical_id !== 'BMG') return f;
            if (naoElegivel) {
                return { ...f, status: 'CONCLUIDO' as FinanceiraStatus, probabilidade: 'baixa' as const, hidden: true, erro: undefined };
            }
            if (!approved) {
                return { ...f, status: 'AGUARDANDO_CODIGO' as FinanceiraStatus, erro: 'Código inválido. Verifique e tente novamente.' };
            }
            return {
                ...f,
                status: 'CONCLUIDO' as FinanceiraStatus,
                probabilidade: (hasOptionalData ? 'alta' : 'disponivel') as 'alta' | 'disponivel',
                valor: data.valor_disponivel ?? undefined,
                // O BMG devolve valor único (não faixa min/max) pra prazo e parcela — só
                // prazo_min/parcela_min são preenchidos, deixando o _max undefined, pra cair
                // no fallback de valor único do card em vez de exibir "48x – 48x".
                prazo_min: data.quantidade_parcelas ?? undefined,
                prazo_max: undefined,
                parcela_min: data.maximo_parcela ?? undefined,
                parcela_max: undefined,
                selecionada: true,
            };
        }));
        return approved;
    }, [screeningId, hasOptionalData]);


    const handleSimulate = useCallback(async (params: SimulacaoParams): Promise<void> => {
        if (screeningId == null) return;
        setResultados([]);
        setSimulateError(false);
        setIsLoadingSimulation(true);

        const financeirasParams = (params.financeiras as string[]);
        const payload = {
            journey_id: String(screeningId),
            modo: params.valor_desejado != null ? 'VALOR_DESEJADO' as const : 'VALOR_PARCELA' as const,
            valor: (params.valor_desejado ?? params.valor_parcela ?? 0),
            qtde_parcelas: params.num_parcelas ?? 0,
            financeiras: financeirasParams,
        };
        const [response, error] = await service.simulateScreening(payload);
        setIsLoadingSimulation(false);
        if (error) {
            if (checkCompetenceWindow(response)) return;
            toast.error(response?.message || error);
            setSimulateError(true);
            return;
        }
        // Extrai RESULTADOS do formato: { data: { data: { RESULTADOS: [...] } } }
        const resultados = response?.data?.data?.RESULTADOS
            ?? response?.data?.RESULTADOS
            ?? response?.RESULTADOS
            ?? response?.data
            ?? [];
        setResultados(resultados);
    }, [screeningId]);

    // Busca o schema do formulário de digitação (campos variam por financeira).
    const handleLoadDigitacaoForm = useCallback(async (): Promise<boolean> => {
        if (screeningId == null || ofertaSelecionada == null || formData == null) return false;
        setIsLoadingDigitacaoForm(true);
        const [response, error] = await service.getDigitationForm({
            journey_id: String(screeningId),
            financeira: ofertaSelecionada.financeira,
            cpf: formData.cpf.replace(/\D/g, ''),
            uf: formData.estado,
        });
        setIsLoadingDigitacaoForm(false);
        if (error) {
            toast.error(response?.message || error);
            return false;
        }
        const data = response?.data?.data ?? response?.data ?? {};
        const fields: DigitacaoFormField[] = data.formulario ?? [];
        setDigitacaoForm(fields);
        setDigitacaoIdCadastro(data.id_cadastro ?? null);
        // AI-1109: pré-preenche (sem exibir) os campos reaproveitados/fixos com o que já
        // foi capturado na jornada, antes mesmo do parceiro abrir o form de digitação.
        // SB-1953: o que sobrar vazio é completado com a última digitação do cliente
        // (campo `historico` do schema) — nessa ordem, pra jornada e valor fixo terem
        // precedência sobre o histórico. Ver applyHistoricoPrefill.
        setFormDataDigitacao((prev) => applyHistoricoPrefill(
            applyDigitacaoInjections(prev, fields, ofertaSelecionada.financeira, { formData }),
            fields
        ));
        return true;
    }, [screeningId, ofertaSelecionada, formData]);

    // Valida os dados digitados junto ao back antes de seguir para a Confirmação
    // (checa se estão adequados para envio ao banco). Em caso de erro, retorna a lista
    // de campos problemáticos para o form marcar cada um.
    const handleValidateDigitacao = useCallback(async (dados: FormDataDigitacao): Promise<{ success: boolean; fieldErrors: DigitacaoValidationFieldError[] }> => {
        if (screeningId == null || ofertaSelecionada == null) return { success: false, fieldErrors: [] };
        setIsValidatingDigitacao(true);
        // Garante que os campos reaproveitados/fixos vão no payload mesmo que não tenham
        // sido registrados no react-hook-form (por estarem fora do form exibido).
        const dadosComInjecoes = applyDigitacaoInjections(dados, digitacaoForm ?? [], ofertaSelecionada.financeira, { formData });
        const [response, error] = await service.validateDigitation({
            journey_id: String(screeningId),
            step: 'VALIDAR',
            payload: {
                ...dadosComInjecoes,
                dados_operacao: { oferta_escolhida: ofertaSelecionada.id },
            },
        });
        setIsValidatingDigitacao(false);

        // BEVI-807 — a recusa da Crefaz (telefone/cidade×CEP/data de emissão) chega com
        // `data: null` e o motivo inteiro concatenado em `message` (confirmado em caso
        // real), não aninhado em `data.mensagem` como o restante do serviço sugeria. Sem
        // ler isso, a tela ficava muda: "Próximo" travava sem nenhum aviso.
        const mensagem: string | undefined =
            (typeof response?.message === 'string' && response.message) ||
            (typeof response?.data?.data?.mensagem === 'string' && response.data.data.mensagem) ||
            (typeof response?.data?.mensagem === 'string' && response.data.mensagem) ||
            undefined;

        // parseFieldErrorsFromMensagem extrai "<chave>: <motivo>" direto da mensagem (caso
        // confirmado); detectFieldKindFromMensagem é só um fallback por palavra-chave pra
        // quando a mensagem não vier nesse formato.
        const fieldErrorsFromMensagem = (): DigitacaoValidationFieldError[] => {
            if (!mensagem) return [];
            const porChave = parseFieldErrorsFromMensagem(mensagem);
            if (porChave.length > 0) return porChave;
            const kind = detectFieldKindFromMensagem(mensagem);
            const campo = kind ? (digitacaoForm ?? []).find((f) => detectFieldKind(f.chave) === kind) : undefined;
            return campo ? [{ field: campo.chave, message: mensagem, type: 'server' }] : [];
        };

        if (error) {
            let fieldErrors: DigitacaoValidationFieldError[] = Array.isArray(response?.data) ? response.data : [];
            if (fieldErrors.length === 0) fieldErrors = fieldErrorsFromMensagem();
            if (fieldErrors.length === 0) toast.error(mensagem || error);
            return { success: false, fieldErrors };
        }

        const status = response?.data?.data?.status ?? response?.data?.status;
        if (status === 'VALIDADO') return { success: true, fieldErrors: [] };

        const fieldErrors = fieldErrorsFromMensagem();
        if (fieldErrors.length === 0 && mensagem) toast.error(mensagem);
        return { success: false, fieldErrors };
    }, [screeningId, ofertaSelecionada, digitacaoForm, formData]);

    // Checa se o próprio parceiro logado é comissionado, antes de liberar a efetivação
    // (ver StepConfirmacao) — manda o partner_code cru (se o usuário for dependente, o
    // backend resolve o sufixo e identifica o parceiro master sozinho).
    const checkLoggedPartnerCommission = useCallback(async (): Promise<void> => {
        setIsCheckingLoggedCommission(true);
        const [response, error] = await service.checkPartnerCommission({
            partner_code: String(userLogged?.partner_code || ''),
        });
        setIsCheckingLoggedCommission(false);
        if (error) {
            toast.error(response?.message || error);
            setLoggedPartnerCommission(null);
            return;
        }
        setLoggedPartnerCommission(response?.data?.data ?? response?.data ?? null);
    }, [userLogged?.partner_code]);

    // Valida o código de um terceiro informado como "agente de digitação", quando o
    // parceiro logado não é comissionado — só guarda o resultado em typingAgent quando
    // a consulta volta comissionado: true (ver TypingAgentField).
    const validateTypingAgentCode = useCallback(async (partnerCode: string): Promise<void> => {
        setIsCheckingTypingAgent(true);
        setTypingAgentError(null);
        setTypingAgent(null);
        const [response, error] = await service.checkPartnerCommission({ partner_code: partnerCode });
        setIsCheckingTypingAgent(false);
        if (error) {
            setTypingAgentError(response?.message || String(error));
            return;
        }
        const data: PartnerCommissionResult | undefined = response?.data?.data ?? response?.data;
        if (!data?.encontrado) {
            setTypingAgentError('Código de parceiro inválido.');
            return;
        }
        if (!data.comissionado) {
            setTypingAgentError('Este parceiro não é comissionado para digitação.');
            return;
        }
        setTypingAgent(data);
    }, []);

    const clearTypingAgent = useCallback(() => {
        setTypingAgent(null);
        setTypingAgentError(null);
    }, []);

    const handleSubmitProposta = useCallback(async (): Promise<void> => {
        if (screeningId == null || ofertaSelecionada == null || formDataDigitacao == null) return;
        setIsSubmittingProposta(true);

        // Se o parceiro logado é comissionado, quem digita é ele mesmo (partner_id próprio,
        // mesmo se for dependente — o backend reconhece e resolve sozinho); senão, é o
        // agente de digitação validado em TypingAgentField (data.codigo, não codigo_parceiro).
        const loggedTypingAgentId = loggedPartnerCommission?.comissionado
            ? (userLogged?.partner_id ? String(userLogged.partner_id) : null)
            : (typingAgent?.comissionado ? String(typingAgent.codigo) : null);

        const dadosComInjecoes = applyDigitacaoInjections(formDataDigitacao, digitacaoForm ?? [], ofertaSelecionada.financeira, { formData });
        const [response, error] = await service.validateDigitation({
            journey_id: String(screeningId),
            step: 'EFETIVAR',
            payload: {
                ...dadosComInjecoes,
                dados_operacao: {
                    oferta_escolhida: ofertaSelecionada.id,
                },
            },
            ...(loggedTypingAgentId ? { logged_typing_agent_id: loggedTypingAgentId } : {}),
        });
        setIsSubmittingProposta(false);
        if (error) {
            setPropostaResultado({ status: 'erro', mensagem: response?.message || String(error) });
            return;
        }
        setPropostaResultado(response?.data?.data ?? response?.data ?? response);
    }, [screeningId, ofertaSelecionada, formDataDigitacao, digitacaoForm, formData, loggedPartnerCommission, typingAgent, userLogged?.partner_id]);

    const startPolling = useCallback((id: string | number) => {
        setIsPolling(true);
        pollingRef.current = setInterval(async () => {
            const [response, error] = await service.getStatus(id);
            if (error) return;

            const updates: FinanceiraCard[] = response?.data ?? response ?? [];
            if (!updates.length) return;

            setFinanceiras((prev) =>
                prev.map((f) => {
                    const upd = updates.find((u: any) => u.id === f.id);
                    if (!upd) return f;
                    return {
                        ...f,
                        status: upd.status ?? f.status,
                        link_url: upd.link_url ?? f.link_url,
                        valor: upd.valor ?? f.valor,
                        prazo_min: upd.prazo_min ?? f.prazo_min,
                        prazo_max: upd.prazo_max ?? f.prazo_max,
                        parcela_min: upd.parcela_min ?? f.parcela_min,
                        parcela_max: upd.parcela_max ?? f.parcela_max,
                        erro: upd.erro ?? f.erro,
                    };
                })
            );

            const allDone = updates.every(
                (u: any) => u.status === 'CONCLUIDO' || u.status === 'ERRO'
            );
            if (allDone) stopPolling();
        }, 3000);
    }, []);

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
        setIsPolling(false);
    }, []);

    // Persiste a sessão em cache a cada mudança relevante, para sobreviver a um F5.
    // Só a partir do step 2: no step 1 o Step1Form já seta formData assim que o usuário
    // clica em "Próximo" (antes de confirmar o modal de LGPD), então cachear nesse ponto
    // criaria uma sessão "presa" no step 1 mesmo sem o formulário ter sido enviado de fato.
    useEffect(() => {
        if (!formData || step < 2) return;
        saveSimulationSession({
            step,
            formData,
            financeiras,
            screeningId,
            simulacaoId,
            resultados,
            simParams,
            hasOptionalData,
            ultimaSimulacao,
            clienteConhecido,
            ofertaSelecionada,
            formDataDigitacao,
            propostaResultado,
            digitacaoForm,
            digitacaoIdCadastro,
            typingAgent,
        });
    }, [step, formData, financeiras, screeningId, simulacaoId, resultados, simParams, hasOptionalData, ultimaSimulacao, clienteConhecido, ofertaSelecionada, formDataDigitacao, propostaResultado, digitacaoForm, digitacaoIdCadastro, typingAgent]);

    const toggleFinanceira = useCallback((id: number | string) => {
        setFinanceiras((prev) =>
            prev.map((f) => (f.id === id ? { ...f, selecionada: !f.selecionada } : f))
        );
    }, []);

    const reset = useCallback(() => {
        stopPolling();
        clearSimulationSession();
        financeirasRef.current = [];
        bmgSelectedRef.current = true;
        expectedFinanceirasRef.current = null;
        setStep(1);
        setFormData(null);
        setFinanceiras([]);
        setScreeningId(null);
        setSimulacaoId(null);
        setResultados([]);
        setSimParams(null);
        setHasOptionalData(true);
        setUltimaSimulacao(null);
        setClienteConhecido(null);
        setOfertaSelecionada(null);
        setFormDataDigitacao(null);
        setPropostaResultado(null);
        setDigitacaoForm(null);
        setDigitacaoIdCadastro(null);
        setCompetenceWindowMessage(null);
        setSimulateError(false);
        setLoggedPartnerCommission(null);
        setTypingAgent(null);
        setTypingAgentError(null);
    }, [stopPolling]);

    return (
        <SimulationBankContext.Provider
            value={{
                step,
                formData,
                financeiras,
                screeningId,
                simulacaoId,
                resultados,
                simParams,
                hasOptionalData,
                ultimaSimulacao,
                clienteConhecido,
                ofertaSelecionada,
                formDataDigitacao,
                propostaResultado,
                digitacaoForm,
                isLoadingTriagem,
                isLoadingSimulation,
                simulateError,
                isSubmittingProposta,
                isLoadingDigitacaoForm,
                isValidatingDigitacao,
                isPolling,
                isValidatingBmg,
                loggedPartnerCommission,
                isCheckingLoggedCommission,
                checkLoggedPartnerCommission,
                typingAgent,
                isCheckingTypingAgent,
                typingAgentError,
                validateTypingAgentCode,
                clearTypingAgent,
                competenceWindowMessage,
                dismissCompetenceWindow,
                checkScreeningAvailability,
                getDataprevBlockStatus,
                setDataprevBlockStatus,
                listFinanceirasParaConsulta,
                getLastSelectedFinancials,
                queryPropostas,
                queryHistoricoSimulacoes,
                setStep,
                setFormData,
                handleTriagem,
                handleScreeningPoll,
                setSimParams,
                handleSimulate,
                setOfertaSelecionada,
                setFormDataDigitacao,
                handleLoadDigitacaoForm,
                handleValidateDigitacao,
                handleSubmitProposta,
                startPolling,
                stopPolling,
                toggleFinanceira,
                handleValidateBmgCode,
                reset,
            }}
        >
            {children}
        </SimulationBankContext.Provider>
    );
};

export const useSimulationBank = () => useContext(SimulationBankContext);
